import { lazy, Suspense } from "react";
import { useCanAccess, useGetList } from "ra-core";

import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardNotifications } from "./DashboardNotifications";
import { HubActivityFeed } from "./HubActivityFeed";
import { HotContacts } from "./HotContacts";
import { RoleStats } from "./RoleStats";

// The charting library is the largest dependency on this page and both charts
// sit below the stat tiles, so they arrive after the page rather than holding
// it up. Worth 167KB off the first paint on a phone.
const DealsChart = lazy(() =>
  import("./DealsChart").then((m) => ({ default: m.DealsChart })),
);
const TicketStatusChart = lazy(() =>
  import("./TicketStatusChart").then((m) => ({ default: m.TicketStatusChart })),
);
const PipelineByStageChart = lazy(() =>
  import("./PipelineByStageChart").then((m) => ({
    default: m.PipelineByStageChart,
  })),
);
const TaskThroughputChart = lazy(() =>
  import("./TaskThroughputChart").then((m) => ({
    default: m.TaskThroughputChart,
  })),
);
import { TasksList } from "./TasksList";
import { Welcome } from "./Welcome";

/**
 * One dashboard, assembled from what the viewer may see.
 *
 * Nothing here branches on a role name. Each section is gated on the permission
 * that guards its own resource, so an admin gets the whole hub, a developer
 * gets their tasks and the throughput chart, and a client gets their projects
 * and tickets -- and a role added later composes itself.
 *
 */
export const Dashboard = () => {
  const canContacts = useCanAccess({ resource: "contacts", action: "list" });
  const canDeals = useCanAccess({ resource: "deals", action: "list" });
  const canTasks = useCanAccess({ resource: "tasks", action: "list" });
  const canTickets = useCanAccess({ resource: "tickets", action: "list" });

  // Only shown when there is something to plot: an empty forecast is worse
  // than no forecast.
  const { total: totalDeal } = useGetList(
    "deals",
    { pagination: { page: 1, perPage: 1 } },
    { enabled: canDeals.canAccess === true },
  );

  // No setup checklist. It was upstream's first-run onboarding -- install the
  // app, add a contact, add a note -- and it reappeared whenever contacts or
  // notes happened to be near zero, which after a database clean is a hub that
  // is fully installed and in daily use. Telling an admin to "install Comet
  // Hub" on an installed hub is noise, and it took the top of the dashboard
  // from the numbers that matter.

  return (
    <div className="flex flex-col gap-6 mt-1">
      <RoleStats />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8 flex flex-col gap-6">
          {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
          {/* Each chart is gated on the resource it reads, so the dashboard
              composes itself per role: marketing gets the pipeline, a
              developer gets throughput and the ticket queue, a client gets
              their project's queue. Nothing branches on a role name. */}
          <Suspense fallback={null}>
            {canDeals.canAccess && totalDeal ? <DealsChart /> : null}
            {canDeals.canAccess ? <PipelineByStageChart /> : null}
            {canTasks.canAccess ? <TaskThroughputChart /> : null}
            {canTickets.canAccess ? <TicketStatusChart /> : null}
          </Suspense>
          {canContacts.canAccess ? <DashboardActivityLog /> : null}
        </div>

        {/* Notifications on the right, as asked: what changed in the viewer's
            own area, next to the numbers describing it. */}
        <div className="lg:col-span-4 flex flex-col gap-6">
          <DashboardNotifications />
          <HubActivityFeed />
          {canContacts.canAccess ? <HotContacts /> : null}
          {canTasks.canAccess ? <TasksList /> : null}
        </div>
      </div>
    </div>
  );
};
